import React, { useEffect, useState } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { errorsSelector, resetResetPassword, sendingFormSelector } from '@src/features/customers';
import { useLogin } from '@src/features/customers/auth/login';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import Input from '@src/components/ui2/Input';
import Button from '@src/components/ui2/Button';
import { ForgotPassword, SubTitle } from './styles';
import ForgotPasswordForm from './ForgotPasswordForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import { ResponsiveModal } from '@src/components/ui2/ResponsiveModal';
import { convertGeneralContextErrorToHookForm } from '@src/common';

type PasswordFormProps = {
  email: string;
};

const PasswordForm = (props: PasswordFormProps) => {
  const { email } = props;

  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const { submitPassword } = useLogin();

  const contextErrors = useAppSelector(errorsSelector);
  const loading = useAppSelector(sendingFormSelector);

  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);

  const { handleSubmit, control, setError, formState } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      email,
      password: '',
    },
    resolver: zodResolver(schema(i18n)),
  });
  const { errors } = formState;

  const submit = (payload: FieldValues) => {
    submitPassword(email, payload['password']);
  };

  const closeForgotPasswordModal = () => {
    dispatch(resetResetPassword());
    setForgotPasswordModalOpen(false);
  };

  useEffect(() => {
    convertGeneralContextErrorToHookForm('password', setError, contextErrors, 'login');
  }, [contextErrors]);

  return (
    <>
      <form onSubmit={handleSubmit(submit)}>
        <SubTitle>{i18n('direct_link_password_form_title')}</SubTitle>

        <Controller
          name='password'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              type='password'
              name={name}
              label={i18n('direct_link_password_form_password_label')}
              value={value}
              onChange={onChange}
              disabled={loading}
              error={!!errors[name]}
              helperText={errors[name]?.message as string}
              {...rest}
            />
          )}
        />

        <Button fullWidth type='submit' loading={loading}>
          {i18n('direct_link_password_form_login')} <MdArrowForward />
        </Button>

        <ForgotPassword onClick={() => setForgotPasswordModalOpen(true)}>Esqueceu a senha?</ForgotPassword>
      </form>

      <ResponsiveModal
        open={forgotPasswordModalOpen}
        onClose={() => closeForgotPasswordModal()}
        title={i18n('direct_link_forgot_password_modal_title')}
        width={400}
      >
        <ForgotPasswordForm email={email!} onCancel={() => closeForgotPasswordModal()} />
      </ResponsiveModal>
    </>
  );
};

export default PasswordForm;
